import React from "react";

/**
 * FilterChips is a React component that displays a list of filter chips.
 * Each chip is a span element that contains a label and a button.
 * The button, when clicked, triggers the handleClose function with the category and filter value as arguments.
 * There is also a reset button that triggers the resetFilters function when clicked.
 *
 * @component
 * @param {Object} props - The properties that define the FilterChips component.
 * @param {Object} props.filters - An object where each key-value pair represents a filter. The key is the filter name and the value is an array of labels.
 * @param {Function} props.onClose - A function that is called when the button in a filter chip is clicked. It receives the filter name and label as arguments.
 * @param {Function} props.onResetFilters - A function that is called when the reset button is clicked.
 * @param {String} props.resetFiltersLabel - The label for the reset button.
 *
 * @returns {React.Element} A list of filter chips.
 */

const ChipElement = ({ category, filterValue, onClose }) => (
    <button
        key={filterValue}
        className="filter-label flex items-center py-2.5 px-5 text-sm text-green bg-green/15 rounded-xs leading-none cursor-pointer"
        onClick={() => onClose(category, filterValue)}
    >
        {filterValue}
        <span className="w-1.75 h-1.75 ml-3.75 bg-closeIconGreen bg-no-repeat bg-center" />
    </button>
);

const FilterChips = ({ filters, onClose, onResetFilters, resetFiltersLabel, enableReset = true }) => (
    <>
        {Object.entries(filters).map(([category, categoryFilters]) => {
            // When multiple filter values available under the category (Ex: Checkboxes)
            if (Array.isArray(categoryFilters)) {
                return categoryFilters?.map((filterValue) => (
                    <ChipElement key={filterValue} category={category} filterValue={filterValue} onClose={onClose} />
                ));
            }
            // When filter value contains a single string (Ex: Keyword search)
            return (
                categoryFilters?.length > 0 && (
                    <ChipElement category={category} filterValue={categoryFilters} onClose={onClose} />
                )
            );
        })}
        {enableReset && (
            <button
                className="flex items-center ml-2.5 pr-5 text-sm text-gray font-bold cursor-pointer bg-closeGray bg-no-repeat bg-right-center"
                onClick={onResetFilters}
            >
                {resetFiltersLabel}
            </button>
        )}
    </>
);

export default FilterChips;
